import React, { useEffect, useRef, useState } from "react";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { Share2, Download } from "lucide-react";
import QRCode from "qrcode";

interface SidebarShareCardProps {
  /** Optional referral link to share */
  referralLink?: string | null;
}

/**
 * Component shown at the bottom of the desktop sidebar.
 * Shows a static share card with QR code for referral link.
 */
export const SidebarShareCard: React.FC<SidebarShareCardProps> = ({
  referralLink,
}) => {
  const launchParams = useLaunchParams();
  const userDisplayName =
    launchParams.initData?.user?.username ||
    launchParams.initData?.user?.firstName ||
    "Trigger User";

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!canvasRef.current || !referralLink) return;

    const generateCard = async () => {
      setIsGenerating(true);
      try {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Set canvas dimensions
        canvas.width = 265;
        canvas.height = 265;

        // Load background image
        const bg = new Image();
        bg.crossOrigin = "anonymous";
        bg.src = "/shareCardBg.png";

        await new Promise((resolve, reject) => {
          bg.onload = resolve;
          bg.onerror = reject;
        });

        // Draw background
        ctx.drawImage(bg, 0, 0, 265, 265);

        // Generate QR code in center
        const qrCanvas = document.createElement("canvas");
        const qrSize = 90;
        await QRCode.toCanvas(qrCanvas, referralLink, {
          width: qrSize,
          margin: 0,
          color: {
            dark: "#000000",
            light: "#FFFFFF",
          },
          errorCorrectionLevel: "M",
        });

        // Draw QR code centered
        const qrX = (265 - qrSize) / 2;
        const qrY = (270 - qrSize) / 2;
        ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

        // Draw username in top right corner
        ctx.font = "bold 14px Inter, system-ui, sans-serif";
        ctx.fillStyle = "#FFFFFF";
        ctx.textAlign = "right";
        ctx.textBaseline = "top";

        // Add shadow for better readability
        ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 1;

        ctx.fillText("@" + userDisplayName, 250, 25);

        // Reset shadow
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
      } catch (error) {
        console.error("Error generating share card:", error);
      } finally {
        setIsGenerating(false);
      }
    };

    generateCard();
  }, [referralLink, userDisplayName]);

  const handleShare = () => {
    if (!referralLink) return;

    if (navigator.share) {
      navigator
        .share({
          title: "Join Trigger",
          text: "Join Trigger - The fastest trading alerts platform! 🚀",
          url: referralLink,
        })
        .catch(console.error);
    } else {
      window.open(referralLink, "_blank");
    }
  };

  const handleDownload = () => {
    if (!canvasRef.current) return;

    try {
      canvasRef.current.toBlob((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `trigger-referral-${Date.now()}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error("Error downloading share card:", error);
    }
  };

  if (!referralLink) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-medium text-text-secondary uppercase tracking-wide">
        Share Your Referral
      </h4>

      <div className="space-y-2">
        {/* Canvas Card */}
        <div className="relative rounded-xl overflow-hidden">
          <canvas
            ref={canvasRef}
            className="w-full h-auto"
            style={{ maxWidth: "265px" }}
          />
          {isGenerating && (
            <div className="absolute inset-0 flex items-center justify-center bg-tertiary/50">
              <div className="text-xs text-text-secondary">Generating...</div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleShare}
            className="flex-1 py-2 px-3 text-sm bg-accent hover:bg-accent/80 text-text-primary font-medium rounded-lg transition-all duration-150 flex items-center justify-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
          <button
            onClick={handleDownload}
            className="py-2 px-3 text-sm bg-tertiary hover:bg-tertiary/80 border border-border text-text-primary font-medium rounded-lg transition-all duration-150 flex items-center justify-center"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
